// Which editor/display widget a screen field gets in the item panel.
//
// With editmeta (the viewer may edit) the field's own schema decides. Without
// it — a status whose workflow makes the item read-only, or a viewer without
// edit rights — the panel used to guess from the field KEY alone, so every
// custom field fell through to 'text' and its stored object printed as raw
// JSON (a select as {"id":…,"value":…}, a user as the whole account, a
// paragraph as its ADF). The field catalogue (/api/fields) knows every
// field's schema, so a read-only screen reads the same as an editable one.

// Stride's "Work item reference" type: schema.type 'string' (its value is one
// work item's key, stored and searched like text), told apart by its custom id.
export const WORKITEMREF_CUSTOM = 'com.zebra.stride:workitemref';

// Determine the editor type from editmeta schema
export function getSchemaEditorType(fieldKey, schema) {
  if (fieldKey === 'status') return 'status-badge';
  if (fieldKey === 'description') return 'rich-text';
  if (fieldKey === 'issuetype') return 'readonly-display';
  if (fieldKey === 'project') return 'readonly-display';
  if (fieldKey === 'parent') return 'readonly-display';
  if (fieldKey === 'created' || fieldKey === 'updated') return 'datetime-display';
  if (fieldKey === 'timeoriginalestimate' || fieldKey === 'timeestimate') return 'time-display';
  if (fieldKey === 'timespent') return 'time-readonly';

  const type = schema?.type;
  const items = schema?.items;
  const custom = schema?.custom;

  // A Line items field draws its own table (modules/database/line-items).
  if (type === 'lineitems') return 'lineitems';
  // A Whiteboard field draws its own board (modules/database/whiteboard).
  if (type === 'whiteboard') return 'whiteboard';
  // A Work item reference draws a read-only card of the work item it names
  // (components/WorkItemRefField) — before the string rule, which would make
  // it a text box. Automations set it; nobody types in it.
  if (custom === WORKITEMREF_CUSTOM) return 'workitemref';

  // Wiki/textarea custom fields should use rich-text editor
  if (custom?.includes('textarea')) return 'rich-text';

  // Date-flavoured fields whose schema.type is something else (e.g. Plans /
  // Advanced Roadmaps' jpo-custom-field-baseline-start reports type:'string'
  // but Jira's backend still rejects '' on save with "must be of the format
  // yyyy-MM-dd"). Match on schema.custom before the generic type checks so
  // these render as date pickers and clear sends null.
  if (typeof custom === 'string' && /date|baseline-(start|end)/i.test(custom)) return 'date';

  if (type === 'string') return 'text';
  if (type === 'date') return 'date';
  if (type === 'datetime') return 'date';
  if (type === 'number') return 'number';
  if (type === 'option') return 'select';
  if (type === 'user') return 'user-display';
  // Several people (Jira's multiuserpicker): its own editor, which adds and
  // removes people instead of replacing the value with one.
  if (type === 'array' && items === 'user') return 'multiuser-display';
  if (schema?.custom?.includes('multiuserpicker')) return 'multiuser-display';
  if (schema?.custom?.includes('userpicker')) return 'user-display';
  if (type === 'array' && items === 'option') return 'multiselect-display';
  if (type === 'array' && items === 'string') return 'labels-display';
  if (type === 'array' && items === 'version') return 'versions-display';
  if (type === 'array' && items === 'component') return 'components-display';
  if (type === 'priority') return 'select';
  if (type === 'resolution') return 'select';

  return 'text';
}

// Guess editor type from field key when we only have the screen layout (no editmeta schema)
export function guessEditorType(fieldKey) {
  if (fieldKey === 'status') return 'status-badge';
  if (fieldKey === 'description') return 'rich-text';
  if (fieldKey === 'issuetype') return 'readonly-display';
  if (fieldKey === 'project') return 'readonly-display';
  if (fieldKey === 'parent') return 'readonly-display';
  if (fieldKey === 'summary') return 'text';
  if (fieldKey === 'duedate') return 'date';
  if (fieldKey === 'priority') return 'select';
  if (fieldKey === 'resolution') return 'select';
  if (fieldKey === 'assignee' || fieldKey === 'reporter') return 'user-display';
  if (fieldKey === 'labels') return 'labels-display';
  if (fieldKey === 'components') return 'components-display';
  if (fieldKey === 'fixVersions') return 'versions-display';
  if (fieldKey === 'environment') return 'text';
  if (fieldKey === 'created' || fieldKey === 'updated') return 'datetime-display';
  if (fieldKey === 'creator') return 'user-display';
  if (fieldKey === 'timeoriginalestimate' || fieldKey === 'timeestimate') return 'time-display';
  if (fieldKey === 'timespent') return 'time-readonly';
  return 'text';
}

// The panel's one decision: editmeta's schema when there is one, else the
// catalogue's schema for the field, else the key-based guess. A Line items
// field keeps its table, a Whiteboard its board and a Work item reference its
// card, either way.
export function resolveScreenEditorType(fieldKey, meta, catalogueField) {
  if (meta) return getSchemaEditorType(fieldKey, meta.schema);
  const schema = catalogueField?.schema;
  if (schema?.type === 'lineitems') return 'lineitems';
  if (schema?.type === 'whiteboard') return 'whiteboard';
  if (schema?.custom === WORKITEMREF_CUSTOM) return 'workitemref';
  const guessed = guessEditorType(fieldKey);
  if (guessed !== 'text' || !schema?.type) return guessed;
  return getSchemaEditorType(fieldKey, schema);
}
